import { useEffect, useRef, useState } from "react";
import useTypingGame, { CharStateType } from "react-typing-game-hook";

const TEST_DURATION = 60;
const TYPING_OPTIONS = { skipCurrentWordOnSpace: false };

const WritingSpeedTest = ({
  onFinish,
  answers,
}: {
  onFinish?: (data: any) => void;
  answers: any;
}) => {
  const TEXT =
    "Anyone who lives in Antarctica is there to work for a scientific research station.Despite the harsh weather, people continue important research on climate, wildlife, oceans, and space. During summer, the population can reach around 4,000 people, while winter teams become much smaller and must carefully manage food, power, communication, and safety every day.";

  const {
    states: { charsState, currIndex },
    actions: { insertTyping, resetTyping, deleteTyping },
  } = useTypingGame(TEXT, TYPING_OPTIONS);

  const isReadOnly = Boolean(answers);
  const containerRef = useRef<HTMLDivElement>(null);

  const [testState, setTestState] = useState({
    timeLeft: TEST_DURATION,
    startTime: null as number | null,
    endTime: null as number | null,
    accuracy: 0,
    timerStarted: false,
    testFinished: false,
    wpm: 0,
    cpm: 0,
    speedPercentage: 0,
  });

  useEffect(() => {
    if (answers) {
      setTestState((prev) => ({
        ...prev,
        ...answers,
        testFinished: true,
        timerStarted: false,
        timeLeft: 0,
      }));
    }
  }, [answers]);

  const startTestTimer = () => {
    if (isReadOnly) return;
    containerRef.current?.focus();
    if (testState.timerStarted) return;

    setTestState((prev) => ({
      ...prev,
      timerStarted: true,
      startTime: Date.now(),
    }));

    const interval = setInterval(() => {
      setTestState((prev) => {
        if (prev.timeLeft <= 1) {
          clearInterval(interval);
          return {
            ...prev,
            timeLeft: 0,
            endTime: Date.now(),
            testFinished: true,
          };
        }
        return { ...prev, timeLeft: prev.timeLeft - 1 };
      });
    }, 1000);
  };

  useEffect(() => {
    if (testState.timeLeft === 0 && testState.testFinished && !isReadOnly) {
      handleTestFinish();
    }
  }, [testState.timeLeft, testState.testFinished]);

  const handleKey = (key: string) => {
    if (isReadOnly || testState.testFinished) return;

    if (!testState.timerStarted) {
      startTestTimer();
    }

    if (key === "Escape") {
      resetTyping();
      return setTestState({
        timeLeft: TEST_DURATION,
        startTime: null,
        endTime: null,
        accuracy: 0,
        timerStarted: false,
        testFinished: false,
        wpm: 0,
        cpm: 0,
        speedPercentage: 0,
      });
    }

    if (key === "Backspace") return deleteTyping(false);
    if (key.length === 1) insertTyping(key);
  };

  const handleTestFinish = () => {
    const endTime = Date.now();
    const attemptedChars = charsState.slice(0, Math.max(currIndex + 1, 0));
    const totalTyped = attemptedChars.filter(
      (c) => c === CharStateType.Correct || c === CharStateType.Incorrect,
    ).length;
    const correctTyped = attemptedChars.filter(
      (c) => c === CharStateType.Correct,
    ).length;
    const accuracy =
      totalTyped === 0 ? 0 : Math.round((correctTyped / totalTyped) * 100);
    const elapsedMinutes = Math.max(
      (testState.startTime
        ? endTime - testState.startTime
        : TEST_DURATION * 1000) /
        1000 /
        60,
      1 / 60,
    );
    const wpm = Math.round(correctTyped / 5 / elapsedMinutes);
    const cpm = Math.round(totalTyped / elapsedMinutes);
    const speedPercentage = calculateTypingSpeedPercentage(accuracy, wpm, cpm);

    setTestState((prev) => ({
      ...prev,
      accuracy,
      wpm,
      cpm,
      speedPercentage,
      endTime,
    }));
    onFinish?.({
      accuracy,
      wpm,
      cpm,
      startTime: testState.startTime,
      endTime,
      speedPercentage,
    });
  };

  function calculateTypingSpeedPercentage(
    accuracy: number,
    wpm: number,
    cpm: number,
  ) {
    const wpmNormalized = (wpm / 100) * 100;
    const cpmNormalized = (cpm / 500) * 100;
    return Math.min(
      100,
      Math.max(0, accuracy * 0.5 + wpmNormalized * 0.3 + cpmNormalized * 0.2),
    );
  }

  // Format seconds as M:SS
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Typed characters so far (for bottom strip)
  const typedLength = currIndex + 1;

  return (
    <div
      className="surface-0 border-1 surface-border border-round overflow-hidden select-none"
      style={{ outline: "none" }}>
      {/* ── Top bar ── */}
      <div className="flex align-items-center justify-content-start px-3 py-2 surface-50 border-bottom-1 surface-border">
        {/* Timer */}
        <span className="text-primary font-bold text-xl writing-test-mono writing-test-timer">
          {formatTime(testState.timeLeft)}
        </span>
      </div>

      {/* ── Target text area ── */}
      <div
        ref={containerRef}
        tabIndex={isReadOnly ? -1 : 0}
        onClick={!isReadOnly ? startTestTimer : undefined}
        onKeyDown={
          !isReadOnly
            ? (e) => {
                handleKey(e.key);
                e.preventDefault();
              }
            : undefined
        }
        className="px-4 py-3 writing-test-mono writing-test-target"
        style={{
          cursor: isReadOnly ? "not-allowed" : "text",
        }}>
        {TEXT.split("").map((char, index) => {
          const state = charsState[index];
          const isCurrent = currIndex + 1 === index;

          let color = "var(--text-color-secondary)"; // not yet typed — dim
          if (state === CharStateType.Correct) color = "var(--text-color)"; // correct — light
          if (state === CharStateType.Incorrect) color = "var(--red-500)"; // incorrect — red

          return (
            <span
              key={index}
              style={{
                color,
                borderBottom: isCurrent
                  ? "2px solid var(--primary-color)"
                  : "none",
                paddingBottom: isCurrent ? "1px" : "0",
              }}>
              {char}
            </span>
          );
        })}
      </div>

      {/* ── Bottom typed strip ── */}
      <div
        onClick={!isReadOnly ? startTestTimer : undefined}
        className="flex align-items-center flex-wrap px-4 py-3 surface-50 border-top-1 surface-border writing-test-mono writing-test-input"
        style={{
          cursor: isReadOnly ? "not-allowed" : "text",
        }}>
        {typedLength > 0 ? (
          <>
            {TEXT.slice(0, typedLength)
              .split("")
              .map((char, index) => {
                const state = charsState[index];
                return (
                  <span
                    key={index}
                    className={
                      state === CharStateType.Incorrect
                        ? "text-red-500"
                        : "text-color"
                    }>
                    {char}
                  </span>
                );
              })}
            {/* blinking caret */}
            <span
              className="writing-test-caret"
              style={{
                animation: "blink 1s step-start infinite",
              }}
            />
          </>
        ) : (
          <span
            className="text-color-secondary"
            style={{
              borderRight:
                !isReadOnly && testState.timerStarted
                  ? "2px solid var(--primary-color)"
                  : "none",
              paddingRight: !isReadOnly && testState.timerStarted ? "2px" : "0",
            }}>
            {isReadOnly ? "—" : "Start typing…"}
          </span>
        )}
      </div>

      {/* ── Results panel ── */}
      {testState.testFinished && (
        <div className="grid surface-50 border-top-1 surface-border text-center m-0 p-3">
          {[
            { icon: "✔", label: "Accuracy", value: `${testState.accuracy}%` },
            { icon: "⌨", label: "CPM", value: String(testState.cpm) },
            { icon: "📝", label: "WPM", value: String(testState.wpm) },
            {
              icon: "⚡",
              label: "Speed",
              value: `${testState.speedPercentage.toFixed(1)}%`,
            },
          ].map(({ icon, label, value }) => (
            <div key={label} className="col-6 sm:col-3">
              <div className="text-primary text-2xl">
                {icon}
              </div>
              <div className="text-color-secondary text-xs mt-1">
                {label}
              </div>
              <div className="text-color text-xl font-bold mt-1">
                {value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CSS keyframe for caret blink */}
      <style>{`
        .writing-test-mono {
          font-family: 'Courier New', Courier, monospace;
        }

        .writing-test-timer {
          min-width: 52px;
        }

        .writing-test-target {
          min-height: 180px;
          font-size: 1.1rem;
          line-height: 2rem;
          outline: none;
          overflow-wrap: break-word;
          word-break: break-word;
          white-space: pre-wrap;
        }

        .writing-test-input {
          min-height: 48px;
          overflow-wrap: break-word;
          word-break: break-word;
          white-space: pre-wrap;
        }

        .writing-test-caret {
          display: inline-block;
          width: 2px;
          height: 1.1em;
          background-color: var(--primary-color);
          vertical-align: text-bottom;
          margin-left: 1px;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }

        @media (max-width: 480px) {
          .writing-test-target {
            min-height: 150px;
            font-size: 1rem;
            line-height: 1.7rem;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          .writing-test-input {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WritingSpeedTest;
